import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { WebSocketProvider } from "./context/WebSocketContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Landing from "./pages/Landing";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import MTGTools from "./pages/MTGTools";
import DNDTools from "./pages/DNDTools";
import HistoryPage from "./pages/HistoryPage";
import CalendarPage from "./pages/CalendarPage";
import FourthDimension from "./pages/FourthDimension";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    const location = useLocation();

    // If user data was passed from AuthCallback, allow access
    if (location.state?.user) {
        return children;
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="scanlines" />
                <div className="terminal-window p-8 text-center">
                    <div className="matrix-loader mx-auto mb-4" />
                    <p className="text-[#00ff41] font-mono">
                        {'>'} LOADING...
                    </p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    return children;
};

// App Router with session_id detection
const AppRouter = () => {
    const location = useLocation();

    // CRITICAL: Detect session_id during render (NOT in useEffect)
    // useEffect runs AFTER first render - too late!
    // This synchronous check prevents race conditions
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }

    return (
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/profile" 
                element={
                    <ProtectedRoute>
                        <Profile />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/mtg" 
                element={
                    <ProtectedRoute>
                        <MTGTools />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/dnd" 
                element={
                    <ProtectedRoute>
                        <DNDTools />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/history" 
                element={
                    <ProtectedRoute>
                        <HistoryPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/calendar" 
                element={
                    <ProtectedRoute>
                        <CalendarPage />
                    </ProtectedRoute>
                } 
            />
            {/* Hidden 4th Dimension page - accessible via secret URL */}
            <Route path="/4th-dimension" element={<FourthDimension />} />
            {/* Catch-all redirect to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <WebSocketProvider>
                        <AppRouter />
                        <Toaster 
                            position="top-right"
                            toastOptions={{
                                style: {
                                    background: '#0a0a0a',
                                    border: '1px solid #003b00',
                                    color: '#00ff41',
                                    fontFamily: 'JetBrains Mono, monospace',
                                },
                            }}
                        />
                    </WebSocketProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
